/**
 * Get area totals
 * @param  {GenerateSubstrateDescription}    args
 * @return {string}
 */

import type { QuoteDocument, OrderItem, QuoteOptions, OptionsDocument, RateOption } from 'paintscout'
import get from 'lodash/get'
import uniq from 'lodash/uniq'

import type { QuoteItemSection } from '../../index'
import { getAreaSubstrateDimension } from '../../util/calculate/get-areaSubstrate-dimension'
import { getInchSuffix } from '../../util/calculate/get-inch-suffix'
import { getItemSection } from '../get-item-section/get-item-section'
import { filterItem } from '../filter-item'
import { getAreaSubstrate } from '../../area-substrates/get-area-substrate'
import { makeHtml } from '../../../../util/makeHtml/makeHtml'
import mergeLabels from '../merge-labels'
import { isInSection } from '../../area-substrates/is-in-section'
import { filterGroupedSubstrate } from './filter-grouped-substrate'
import { getObjectLabels } from '../../../util/get-object-labels'
import { formatCoatCount, resolveProductCoats } from '../resolve-product-coats'

export function generateSubstrateDescription(args: {
  quote: QuoteDocument
  substrateKey: string
  options?: QuoteOptions
  clientOptions: OptionsDocument
  section?: QuoteItemSection
  noReportText?: boolean
  showGroup?: boolean
  groupKey?: string
}): string {
  const { quote, substrateKey, clientOptions, options, noReportText, showGroup, groupKey } = args
  const showQuantities = get(options, 'showQuantities', false)

  const section = args.section ? args.section : 'bid'

  const areaLabels: string[] = []
  const reportText: string[] = []
  const quantities: { [value: string]: number } = {}
  const prices: { [value: string]: number } = {}
  const coats: { [value: string]: number | string } = {}
  const products: { [value: string]: string[] } = {}
  const prep: { [value: string]: number } = {}
  const dimensions: { [value: string]: { value: number; unit: string } } = {}

  quote.order.area.forEach((orderItem: OrderItem) => {
    if (orderItem.type !== 'area') {
      return
    }
    if (orderItem.parent) {
      return
    }
    if (orderItem._deleted) {
      return
    }

    const areaKey = orderItem.key
    const area = quote.areas[areaKey]
    if (!area) {
      return
    }

    if (area.substrateOrder) {
      area.substrateOrder.forEach((substrateOrderItem: OrderItem) => {
        const areaSubstrate = getAreaSubstrate({
          quote,
          options: clientOptions,
          areaKey,
          key: substrateOrderItem.key,
          full: true
        })

        if (!areaSubstrate) {
          return
        }
        const rate: RateOption = areaSubstrate.rate

        let rateKey
        if (quote.substratesGroupBy === 'client_label') {
          rateKey = get(areaSubstrate, 'rate.client_label')
          if (!rateKey) {
            rateKey = get(areaSubstrate, 'rate.label', 'Custom')
          }
          if (!rateKey) {
            rateKey = 'Custom'
          }
          rateKey = rateKey.trim().toLowerCase()
        } else {
          rateKey = areaSubstrate.rate.name === 'custom' ? areaSubstrate.key : areaSubstrate.rate.name
        }

        if (substrateKey.trim().toLowerCase() !== rateKey) {
          return
        }
        const areaSubstrateSection = getItemSection(areaSubstrate)
        if (
          !isInSection({
            areaSubstrateSection,
            areaSection: getItemSection(area),
            section
          })
        ) {
          return null
        }

        const groupFilter = filterGroupedSubstrate({
          quote,
          areaSubstrate,
          section,
          showGroup,
          groupKey
        })
        if (groupFilter) {
          return
        }

        // Dont include in description if areaSubstrate is not in same section
        if (areaSubstrateSection !== section) {
          return
        }

        // Dont include archived areaSubstrates if not the section we are looking for
        if (section !== 'archived' && areaSubstrateSection === 'archived') {
          return
        }

        // Add areaSubstrate to label and reportText
        const label = area.area_label.value
        areaLabels.push(label)
        reportText.push(rate.report_text)

        // Handle materials on areaSubstrate
        let materials = 0
        const allProducts = [
          ...(Object.keys(areaSubstrate?.product ?? {}).length ? [areaSubstrate.product] : []),
          ...(areaSubstrate?.products ?? [])
        ]
        for (const product of allProducts) {
          if (product?.totalPrice_override && typeof product?.totalPrice_override_value === 'number') {
            materials = product.totalPrice_override_value
          } else {
            materials = Number(product?.totalPrice ?? 0)
          }
        }

        // Handle products on areaSubstrate
        if (!products[label]) {
          products[label] = []
        }

        // A product may carry its own coats override (see resolveProductCoats). When they disagree
        // and products are shown with coats enabled, fold each product's coats into its own text
        // below instead of summing an ambiguous single number into `coats[label]`.
        const { distinct: distinctSubstrateProductCoats, isMixed: substrateCoatsAreMixed } = resolveProductCoats(
          areaSubstrate.coats,
          allProducts
        )
        const foldCoatsIntoProducts =
          substrateCoatsAreMixed && areaSubstrate.showProduct && areaSubstrate.showCoats && areaSubstrate.rate?.useCoats
        const resolvedSubstrateCoats =
          distinctSubstrateProductCoats.length === 1 ? distinctSubstrateProductCoats[0] : areaSubstrate.coats

        for (const areaSubstrateProduct of allProducts) {
          const productParts = [areaSubstrateProduct?.product?.label ?? '', areaSubstrateProduct?.product?.color ?? '']
          if (foldCoatsIntoProducts) {
            productParts.push(formatCoatCount(Number(areaSubstrateProduct?.coats ?? areaSubstrate.coats)))
          }
          const productString = productParts.filter((part) => part).join(' - ')
          if (productString && areaSubstrate.showProduct && !products?.[label]?.includes(productString)) {
            products[label].push(productString)
          }
        }

        // Handle price on areaSubstrate
        let price = 0
        if (areaSubstrate.override_price && typeof areaSubstrate.override_price_value === 'number') {
          price = areaSubstrate.override_price_value
        } else if (areaSubstrate.override_price) {
          price = areaSubstrate.price ?? 0
        } else {
          price = areaSubstrate.default_price ?? 0
        }

        if (!prices[label]) {
          prices[label] = 0
        }
        if (areaSubstrate.showPrice) {
          prices[label] += price + materials
        }

        // Handle dimensions on areaSubstrate
        if (rate.rate_type.includes('ft') && areaSubstrate.showDimensions) {
          const defaultDimensionValue = getAreaSubstrateDimension({
            length: Number(areaSubstrate.length),
            width: Number(areaSubstrate.width),
            height: Number(areaSubstrate.height),
            isRoom: area.area_type.value === 'room',
            rateType: rate.rate_type,
            useInches: areaSubstrate.useInches ?? false
          })

          const dimensionValue = areaSubstrate.override_dimensions
            ? areaSubstrate.override_dimensions_value
            : defaultDimensionValue

          // The (IN) suffix marks an inch-measured surface and is shown regardless of an
          // override, matching the Area dialog's getDimension convention (an override only
          // replaces the value, not the inch annotation).
          const objectLabels = getObjectLabels({ options: clientOptions })
          const inchSuffix = getInchSuffix({ useInches: areaSubstrate.useInches, unitValue: objectLabels.unit.value })

          dimensions[label] = {
            value: (dimensions?.[label]?.value ?? 0) + dimensionValue,
            unit: `${rate.rate_type.includes('lnft') ? 'lnft' : 'sqft'}${inchSuffix}`
          }
        }

        // Handle coats on areaSubstrate. When the products disagree there's no single correct
        // number to sum into `coats[label]`: if products are shown, their coats are folded into
        // `products[label]` above instead; if hidden, show the distinct coat counts as a joined
        // list (e.g. "1, 3") rather than an ambiguous single number.
        if (areaSubstrate.showCoats && areaSubstrate.rate?.useCoats) {
          if (substrateCoatsAreMixed) {
            if (!areaSubstrate.showProduct) {
              const mixedCoatsLabel = distinctSubstrateProductCoats.join(', ')
              coats[label] = coats[label] ? `${coats[label]}, ${mixedCoatsLabel}` : mixedCoatsLabel
            }
          } else {
            // A prior substrate on this label may have already written a joined mixed-coats string
            // above; append to it instead of resetting to 0 and silently dropping it.
            const numericContribution = Number(resolvedSubstrateCoats ?? 0)
            coats[label] =
              typeof coats[label] === 'string'
                ? `${coats[label]}, ${numericContribution}`
                : (coats[label] ?? 0) + numericContribution
          }
        }

        // Handle prep on areaSubstrate
        if (areaSubstrate.showPrep && areaSubstrate.prep) {
          prep[label] = (prep?.[label] ?? 0) + Number(areaSubstrate?.prep ?? 0)
        }

        // Handle Quantities on areaSubstrate
        if (showQuantities && !areaSubstrate.hideQuantity && rate.rate_type === 'quantity') {
          if (!quantities[label]) {
            quantities[label] = 0
          }

          quantities[label] += Number(areaSubstrate.quantity)
        }
      })
    }

    // If nothing added and not in section, skip area
    if (!filterItem(area, section) && !areaLabels.length) {
      return
    }
  })

  // Combine labels to create description
  const descriptionElements = mergeLabels({
    options: clientOptions,
    areaLabels,
    quantities,
    dimensions,
    prices,
    coats,
    prep,
    products
  })

  // Combine all descriptions and reports
  const joinedDescriptionElements = descriptionElements.join(', ')
  const htmlDescription = makeHtml(joinedDescriptionElements)

  const reportTextItems = noReportText
    ? []
    : uniq(reportText.filter((item) => item)).map((item) => {
        return makeHtml(item, { replaceBreaks: true })
      })

  const ret = [htmlDescription, reportTextItems.join('')].filter((item) => item).join('')
  return ret
}
